import { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Typography,
  Box,
  Paper,
  Stack,
  Alert,
  IconButton,
  Chip,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Tooltip,
  Menu,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import {
  Add,
  Delete,
  CheckCircle,
  GridOn,
  GridOff,
  ZoomIn,
  ZoomOut,
  FitScreen,
  MoreVert,
  ContentCopy,
  CloudUpload,
} from '@mui/icons-material';

interface RichMenuEditorProps {
  open: boolean;
  mode: 'create' | 'edit';
  initialData?: any;
  onClose: () => void;
  onSave: (data: any, imageFile: File | null) => Promise<void>;
  onShowSnackbar: (message: string, severity: 'success' | 'error' | 'info') => void;
}

export default function RichMenuEditor({
  open,
  mode,
  initialData,
  onClose,
  onSave,
  onShowSnackbar
}: RichMenuEditorProps) {
  const [currentTab, setCurrentTab] = useState(0);
  const [form, setForm] = useState({
    name: initialData?.name || '',
    alias: initialData?.alias || '',
    chat_bar_text: initialData?.chat_bar_text || '',
    selected: initialData?.selected || false,
    size: initialData?.size || { width: 2500, height: 1686 },
    areas: initialData?.areas || [],
    description: initialData?.description || '',
  });
  
  const [image, setImage] = useState<string | null>(initialData?.image_url || null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [selectedArea, setSelectedArea] = useState<number | null>(null);
  const [canvasScale, setCanvasScale] = useState(0.4);
  const [showGrid, setShowGrid] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templateMenu, setTemplateMenu] = useState<null | HTMLElement>(null);
  
  // Dragging state
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [resizeHandle, setResizeHandle] = useState<string | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);

  // Common LIFF URLs
  const commonActions = [
    { label: '🏠 Home', type: 'uri', uri: 'line://app/home' },
    { label: '📋 Alumni Portal', type: 'uri', uri: 'https://liff.line.me/YOUR_LIFF_ID' },
    { label: '📰 News', type: 'message', text: 'ข่าวสาร' },
    { label: '📞 Contact', type: 'message', text: 'ติดต่อ' },
    { label: '❓ Help', type: 'message', text: 'ช่วยเหลือ' },
  ];

  // Reset form when initialData changes
  useEffect(() => {
    if (initialData) {
      setForm({
        name: initialData.name || '',
        alias: initialData.alias || '',
        chat_bar_text: initialData.chat_bar_text || '',
        selected: initialData.selected || false,
        size: initialData.size || { width: 2500, height: 1686 },
        areas: initialData.areas || [],
        description: initialData.description || '',
      });
      if (initialData.image_url) {
        setImage(initialData.image_url);
      }
    }
  }, [initialData]);

  // Handle mouse events for dragging areas
  const handleMouseDown = (e: React.MouseEvent, index: number, action: 'move' | 'resize', handle?: string) => {
    e.stopPropagation();
    setSelectedArea(index);
    
    if (action === 'move') {
      setIsDragging(true);
    } else {
      setIsResizing(true);
      setResizeHandle(handle || null);
    }
    
    const rect = canvasRef.current?.getBoundingClientRect();
    if (rect) {
      setDragStart({
        x: (e.clientX - rect.left) / canvasScale,
        y: (e.clientY - rect.top) / canvasScale
      });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging && !isResizing) return;
    if (selectedArea === null) return;
    
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    const currentX = (e.clientX - rect.left) / canvasScale;
    const currentY = (e.clientY - rect.top) / canvasScale;
    const deltaX = currentX - dragStart.x;
    const deltaY = currentY - dragStart.y;
    
    const newAreas = [...form.areas];
    const area = newAreas[selectedArea];
    
    if (isDragging) {
      // Move the area
      area.bounds.x = Math.max(0, Math.min(form.size.width - area.bounds.width, area.bounds.x + deltaX));
      area.bounds.y = Math.max(0, Math.min(form.size.height - area.bounds.height, area.bounds.y + deltaY));
    } else if (isResizing && resizeHandle) {
      // Resize based on handle
      const minSize = 50;
      
      if (resizeHandle.includes('e')) {
        const newWidth = Math.max(minSize, area.bounds.width + deltaX);
        area.bounds.width = Math.min(newWidth, form.size.width - area.bounds.x);
      }
      if (resizeHandle.includes('s')) {
        const newHeight = Math.max(minSize, area.bounds.height + deltaY);
        area.bounds.height = Math.min(newHeight, form.size.height - area.bounds.y);
      }
      if (resizeHandle.includes('w')) {
        const newX = area.bounds.x + deltaX;
        const newWidth = area.bounds.width - deltaX;
        if (newWidth >= minSize && newX >= 0) {
          area.bounds.x = newX;
          area.bounds.width = newWidth;
        }
      }
      if (resizeHandle.includes('n')) {
        const newY = area.bounds.y + deltaY;
        const newHeight = area.bounds.height - deltaY;
        if (newHeight >= minSize && newY >= 0) {
          area.bounds.y = newY;
          area.bounds.height = newHeight;
        }
      }
    }
    
    // Round to integers
    area.bounds.x = Math.round(area.bounds.x);
    area.bounds.y = Math.round(area.bounds.y);
    area.bounds.width = Math.round(area.bounds.width);
    area.bounds.height = Math.round(area.bounds.height);
    
    setForm({ ...form, areas: newAreas });
    setDragStart({ x: currentX, y: currentY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeHandle(null);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 1024 * 1024) {
        onShowSnackbar('Image size must be less than 1MB', 'error');
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImage(reader.result as string);
      };
      reader.readAsDataURL(file);
      onShowSnackbar('Image loaded successfully', 'success');
    }
  };

  const applyTemplate = (templateType: string) => {
    const templates: any = {
      '2x2': {
        size: { width: 2500, height: 1686 },
        areas: [
          { bounds: { x: 0, y: 0, width: 1250, height: 843 }, action: { type: 'uri', uri: 'https://example.com/1' } },
          { bounds: { x: 1250, y: 0, width: 1250, height: 843 }, action: { type: 'uri', uri: 'https://example.com/2' } },
          { bounds: { x: 0, y: 843, width: 1250, height: 843 }, action: { type: 'uri', uri: 'https://example.com/3' } },
          { bounds: { x: 1250, y: 843, width: 1250, height: 843 }, action: { type: 'uri', uri: 'https://example.com/4' } }
        ]
      },
      '3x2': {
        size: { width: 2500, height: 1686 },
        areas: [
          { bounds: { x: 0, y: 0, width: 833, height: 843 }, action: { type: 'uri', uri: 'https://example.com/1' } },
          { bounds: { x: 833, y: 0, width: 834, height: 843 }, action: { type: 'uri', uri: 'https://example.com/2' } },
          { bounds: { x: 1667, y: 0, width: 833, height: 843 }, action: { type: 'uri', uri: 'https://example.com/3' } },
          { bounds: { x: 0, y: 843, width: 833, height: 843 }, action: { type: 'uri', uri: 'https://example.com/4' } },
          { bounds: { x: 833, y: 843, width: 834, height: 843 }, action: { type: 'uri', uri: 'https://example.com/5' } },
          { bounds: { x: 1667, y: 843, width: 833, height: 843 }, action: { type: 'uri', uri: 'https://example.com/6' } }
        ]
      },
      'single': {
        size: { width: 2500, height: 843 },
        areas: [
          { bounds: { x: 0, y: 0, width: 2500, height: 843 }, action: { type: 'uri', uri: 'https://example.com' } }
        ]
      },
      '2x1': {
        size: { width: 2500, height: 843 },
        areas: [
          { bounds: { x: 0, y: 0, width: 1250, height: 843 }, action: { type: 'uri', uri: 'https://example.com/1' } },
          { bounds: { x: 1250, y: 0, width: 1250, height: 843 }, action: { type: 'uri', uri: 'https://example.com/2' } }
        ]
      }
    };

    if (templates[templateType]) {
      setForm({
        ...form,
        size: templates[templateType].size,
        areas: templates[templateType].areas
      });
      onShowSnackbar(`Applied ${templateType} template`, 'success');
      setTemplateMenu(null);
    }
  };

  const handleSave = async () => {
    if (!form.name || !form.chat_bar_text) {
      onShowSnackbar('Please fill in required fields', 'error');
      return;
    }

    setSaving(true);
    try {
      await onSave(form, imageFile);
    } finally {
      setSaving(false);
    }
  };

  const deleteArea = (index: number) => {
    const newAreas = form.areas.filter((_: any, i: number) => i !== index);
    setForm({ ...form, areas: newAreas });
    if (selectedArea === index) setSelectedArea(null);
  };

  const duplicateArea = (index: number) => {
    const area = form.areas[index];
    const newArea = {
      ...area,
      bounds: {
        ...area.bounds,
        x: area.bounds.x + 50,
        y: area.bounds.y + 50
      }
    };
    setForm({ ...form, areas: [...form.areas, newArea] });
    onShowSnackbar('Area duplicated', 'success');
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          {mode === 'create' ? '🎨 Create Rich Menu' : '✏️ Edit Rich Menu'}
        </Box>
        <Box>
          <Tooltip title={showGrid ? 'Hide Grid' : 'Show Grid'}>
            <IconButton size="small" onClick={() => setShowGrid(!showGrid)}>
              {showGrid ? <GridOn /> : <GridOff />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Templates">
            <IconButton size="small" onClick={(e) => setTemplateMenu(e.currentTarget)}>
              <MoreVert />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={templateMenu}
            open={Boolean(templateMenu)}
            onClose={() => setTemplateMenu(null)}
          >
            <MenuItem onClick={() => applyTemplate('single')}>📱 Single Button</MenuItem>
            <MenuItem onClick={() => applyTemplate('2x1')}>⚡ 2×1 Grid (Compact)</MenuItem>
            <MenuItem onClick={() => applyTemplate('2x2')}>🎯 2×2 Grid</MenuItem>
            <MenuItem onClick={() => applyTemplate('3x2')}>🎨 3×2 Grid</MenuItem>
          </Menu>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Grid container sx={{ height: '100%' }}>
          {/* Left Sidebar - Form and Areas */}
          <Grid item xs={12} md={4} sx={{ borderRight: 1, borderColor: 'divider', height: '100%', overflow: 'auto' }}>
            <Box sx={{ p: 2 }}>
              <Tabs value={currentTab} onChange={(_, v) => setCurrentTab(v)} variant="fullWidth">
                <Tab label="Settings" />
                <Tab label={`Areas (${form.areas.length})`} />
              </Tabs>

              {/* Tab 0: Settings */}
              {currentTab === 0 && (
                <Box sx={{ mt: 2 }}>
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      label="Menu Name *"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="e.g., Main Menu"
                      required
                    />

                    <TextField
                      fullWidth
                      label="Chat Bar Text *"
                      value={form.chat_bar_text}
                      onChange={(e) => setForm({ ...form, chat_bar_text: e.target.value })}
                      placeholder="Menu text shown in chat"
                      required
                      helperText="Max 14 characters"
                      inputProps={{ maxLength: 14 }}
                    />

                    <TextField
                      fullWidth
                      label="Alias (Optional)"
                      value={form.alias}
                      onChange={(e) => setForm({ ...form, alias: e.target.value })}
                      placeholder="e.g., main-menu"
                    />

                    <Divider />

                    <Typography variant="subtitle2" fontWeight={600}>
                      Canvas Size
                    </Typography>
                    <Stack direction="row" spacing={2}>
                      <TextField
                        type="number"
                        label="Width"
                        value={form.size.width}
                        onChange={(e) => setForm({ 
                          ...form, 
                          size: { ...form.size, width: parseInt(e.target.value) || 2500 }
                        })}
                        InputProps={{ inputProps: { min: 800, max: 2500 } }}
                      />
                      <TextField
                        type="number"
                        label="Height"
                        value={form.size.height}
                        onChange={(e) => setForm({ 
                          ...form, 
                          size: { ...form.size, height: parseInt(e.target.value) || 1686 }
                        })}
                        InputProps={{ inputProps: { min: 843, max: 2500 } }}
                      />
                    </Stack>
                    <Alert severity="info" icon={false}>
                      💡 Standard: 2500×1686 (large) or 2500×843 (compact)
                    </Alert>

                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      label="Description"
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      placeholder="Internal note"
                    />

                    <Divider />

                    <Button
                      variant="outlined"
                      component="label"
                      fullWidth
                      startIcon={<CloudUpload />}
                      color={image ? 'success' : 'primary'}
                    >
                      {image ? 'Change Image' : 'Upload Background'}
                      <input
                        type="file"
                        hidden
                        accept="image/png,image/jpeg"
                        onChange={handleImageUpload}
                      />
                    </Button>

                    {image && (
                      <Box>
                        <Alert severity="success" icon={<CheckCircle />}>
                          {imageFile?.name}
                        </Alert>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => {
                            setImage(null);
                            setImageFile(null);
                          }}
                          sx={{ mt: 1 }}
                        >
                          Remove
                        </Button>
                      </Box>
                    )}
                  </Stack>
                </Box>
              )}

              {/* Tab 1: Areas */}
              {currentTab === 1 && (
                <Box sx={{ mt: 2 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => {
                      const newArea = {
                        bounds: { x: 100, y: 100, width: 500, height: 400 },
                        action: { type: 'uri', uri: 'https://example.com' }
                      };
                      setForm({ ...form, areas: [...form.areas, newArea] });
                      setSelectedArea(form.areas.length);
                      onShowSnackbar('Area added', 'success');
                    }}
                    sx={{ mb: 2 }}
                  >
                    Add Area
                  </Button>

                  {form.areas.length === 0 ? (
                    <Alert severity="info">
                      No areas yet. Click "Add Area" or use templates to get started.
                    </Alert>
                  ) : (
                    <List>
                      {form.areas.map((area: any, index: number) => (
                        <Paper
                          key={index}
                          sx={{
                            mb: 1,
                            border: 2,
                            borderColor: selectedArea === index ? 'primary.main' : 'transparent',
                            cursor: 'pointer'
                          }}
                        >
                          <ListItem onClick={() => setSelectedArea(index)}>
                            <ListItemText
                              primary={
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Chip label={index + 1} size="small" color="primary" />
                                  <Typography variant="body2">
                                    {area.label || `${area.action.type === 'uri' ? '🔗' : area.action.type === 'message' ? '💬' : '📤'} ${area.action.type}`}
                                  </Typography>
                                </Box>
                              }
                              secondary={
                                <Typography variant="caption" display="block">
                                  {area.bounds.x}, {area.bounds.y} · {area.bounds.width}×{area.bounds.height}
                                  {area.label && ` · ${area.action.type}`}
                                </Typography>
                              }
                            />
                            <ListItemSecondaryAction>
                              <Tooltip title="Duplicate">
                                <IconButton size="small" onClick={() => duplicateArea(index)}>
                                  <ContentCopy fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton size="small" color="error" onClick={() => deleteArea(index)}>
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </ListItemSecondaryAction>
                          </ListItem>

                          {selectedArea === index && (
                            <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
                              <Stack spacing={2}>
                                {/* Area Label */}
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Area Label (optional)"
                                  placeholder="e.g. Home Button, Contact Us"
                                  value={area.label || ''}
                                  onChange={(e) => {
                                    const newAreas = [...form.areas];
                                    newAreas[index].label = e.target.value;
                                    setForm({ ...form, areas: newAreas });
                                  }}
                                  helperText="Give this area a name to identify it easily"
                                />
                                
                                <Divider />
                                
                                <TextField
                                  fullWidth
                                  select
                                  size="small"
                                  label="Action Type"
                                  value={area.action.type}
                                  onChange={(e) => {
                                    const newAreas = [...form.areas];
                                    newAreas[index].action = {
                                      type: e.target.value,
                                      ...(e.target.value === 'uri' ? { uri: 'https://example.com' } : {}),
                                      ...(e.target.value === 'message' ? { text: 'Hello' } : {}),
                                      ...(e.target.value === 'postback' ? { data: 'action=example' } : {})
                                    };
                                    setForm({ ...form, areas: newAreas });
                                  }}
                                  SelectProps={{ native: true }}
                                >
                                  <option value="uri">🔗 Open URL</option>
                                  <option value="message">💬 Send Message</option>
                                  <option value="postback">📤 Postback</option>
                                </TextField>

                                {area.action.type === 'uri' && (
                                  <>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      label="URL"
                                      value={area.action.uri || ''}
                                      onChange={(e) => {
                                        const newAreas = [...form.areas];
                                        newAreas[index].action.uri = e.target.value;
                                        setForm({ ...form, areas: newAreas });
                                      }}
                                    />
                                    <Box>
                                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                        Quick Actions:
                                      </Typography>
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {commonActions.filter(a => a.type === 'uri').map((action, i) => (
                                          <Chip
                                            key={i}
                                            label={action.label}
                                            size="small"
                                            variant="outlined"
                                            onClick={() => {
                                              const newAreas = [...form.areas];
                                              newAreas[index].action.uri = action.uri;
                                              setForm({ ...form, areas: newAreas });
                                            }}
                                            sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                                          />
                                        ))}
                                      </Box>
                                    </Box>
                                  </>
                                )}

                                {area.action.type === 'message' && (
                                  <>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      label="Message"
                                      value={area.action.text || ''}
                                      onChange={(e) => {
                                        const newAreas = [...form.areas];
                                        newAreas[index].action.text = e.target.value;
                                        setForm({ ...form, areas: newAreas });
                                      }}
                                    />
                                    <Box>
                                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                        Quick Messages:
                                      </Typography>
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {commonActions.filter(a => a.type === 'message').map((action, i) => (
                                          <Chip
                                            key={i}
                                            label={action.label}
                                            size="small"
                                            variant="outlined"
                                            onClick={() => {
                                              const newAreas = [...form.areas];
                                              newAreas[index].action.text = action.text;
                                              setForm({ ...form, areas: newAreas });
                                            }}
                                            sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                                          />
                                        ))}
                                      </Box>
                                    </Box>
                                  </>
                                )}

                                {area.action.type === 'postback' && (
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="Data"
                                    value={area.action.data || ''}
                                    onChange={(e) => {
                                      const newAreas = [...form.areas];
                                      newAreas[index].action.data = e.target.value;
                                      setForm({ ...form, areas: newAreas });
                                    }}
                                  />
                                )}

                                <Divider />
                                <Typography variant="caption" color="text.secondary">
                                  Position & Size
                                </Typography>
                                <Grid container spacing={1}>
                                  <Grid item xs={6}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      type="number"
                                      label="X"
                                      value={area.bounds.x}
                                      onChange={(e) => {
                                        const newAreas = [...form.areas];
                                        newAreas[index].bounds.x = parseInt(e.target.value) || 0;
                                        setForm({ ...form, areas: newAreas });
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={6}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      type="number"
                                      label="Y"
                                      value={area.bounds.y}
                                      onChange={(e) => {
                                        const newAreas = [...form.areas];
                                        newAreas[index].bounds.y = parseInt(e.target.value) || 0;
                                        setForm({ ...form, areas: newAreas });
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={6}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      type="number"
                                      label="Width"
                                      value={area.bounds.width}
                                      onChange={(e) => {
                                        const newAreas = [...form.areas];
                                        newAreas[index].bounds.width = parseInt(e.target.value) || 0;
                                        setForm({ ...form, areas: newAreas });
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={6}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      type="number"
                                      label="Height"
                                      value={area.bounds.height}
                                      onChange={(e) => {
                                        const newAreas = [...form.areas];
                                        newAreas[index].bounds.height = parseInt(e.target.value) || 0;
                                        setForm({ ...form, areas: newAreas });
                                      }}
                                    />
                                  </Grid>
                                </Grid>
                              </Stack>
                            </Box>
                          )}
                        </Paper>
                      ))}
                    </List>
                  )}
                </Box>
              )}
            </Box>
          </Grid>

          {/* Right Panel - Canvas */}
          <Grid item xs={12} md={8} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', gap: 1, alignItems: 'center' }}>
              <Tooltip title="Zoom Out">
                <IconButton size="small" onClick={() => setCanvasScale(Math.max(0.2, canvasScale - 0.1))}>
                  <ZoomOut />
                </IconButton>
              </Tooltip>
              <Chip label={`${Math.round(canvasScale * 100)}%`} size="small" />
              <Tooltip title="Zoom In">
                <IconButton size="small" onClick={() => setCanvasScale(Math.min(1, canvasScale + 0.1))}>
                  <ZoomIn />
                </IconButton>
              </Tooltip>
              <Tooltip title="Fit to Screen">
                <IconButton size="small" onClick={() => setCanvasScale(0.4)}>
                  <FitScreen />
                </IconButton>
              </Tooltip>
              <Box flexGrow={1} />
              <Typography variant="caption" color="text.secondary">
                {form.size.width} × {form.size.height}px
              </Typography>
            </Box>

            <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2, bgcolor: 'grey.50', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {image ? (
                <Box
                  ref={canvasRef}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                  sx={{
                    position: 'relative',
                    width: form.size.width * canvasScale,
                    height: form.size.height * canvasScale,
                    backgroundImage: `url(${image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    border: '2px solid',
                    borderColor: 'divider',
                    boxShadow: 3,
                    userSelect: 'none',
                    ...(showGrid && {
                      backgroundImage: `
                        linear-gradient(rgba(0,0,0,0.05) 1px, transparent 1px),
                        linear-gradient(90deg, rgba(0,0,0,0.05) 1px, transparent 1px),
                        url(${image})
                      `,
                      backgroundSize: `${50 * canvasScale}px ${50 * canvasScale}px, ${50 * canvasScale}px ${50 * canvasScale}px, cover`,
                      backgroundPosition: '0 0, 0 0, center'
                    })
                  }}
                >
                  {form.areas.map((area: any, index: number) => (
                    <Box
                      key={index}
                      onMouseDown={(e) => handleMouseDown(e, index, 'move')}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedArea(index);
                      }}
                      sx={{
                        position: 'absolute',
                        left: area.bounds.x * canvasScale,
                        top: area.bounds.y * canvasScale,
                        width: area.bounds.width * canvasScale,
                        height: area.bounds.height * canvasScale,
                        border: 3,
                        borderColor: selectedArea === index ? 'primary.main' : 'warning.main',
                        bgcolor: selectedArea === index ? 'rgba(25, 118, 210, 0.15)' : 'rgba(255, 152, 0, 0.1)',
                        cursor: isDragging && selectedArea === index ? 'grabbing' : 'grab',
                        transition: isDragging || isResizing ? 'none' : 'all 0.2s',
                        '&:hover': {
                          bgcolor: 'rgba(25, 118, 210, 0.25)',
                          borderColor: 'primary.main',
                          zIndex: 10
                        }
                      }}
                    >
                      {/* Area number badge */}
                      <Chip
                        label={area.label ? `${index + 1}: ${area.label}` : index + 1}
                        size="small"
                        color={selectedArea === index ? 'primary' : 'warning'}
                        sx={{
                          position: 'absolute',
                          top: 4,
                          left: 4,
                          fontWeight: 700,
                          pointerEvents: 'none'
                        }}
                      />
                      
                      {/* Action type label */}
                      <Typography
                        variant="caption"
                        sx={{
                          position: 'absolute',
                          bottom: 4,
                          left: 4,
                          bgcolor: 'rgba(0,0,0,0.7)',
                          color: 'white',
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          fontSize: '0.65rem',
                          pointerEvents: 'none'
                        }}
                      >
                        {area.action.type === 'uri' ? '🔗' : area.action.type === 'message' ? '💬' : '📤'} {area.action.type}
                      </Typography>
                      
                      {/* Resize handles - only show for selected area */}
                      {selectedArea === index && (
                        <>
                          {/* Corner handles */}
                          <Box
                            onMouseDown={(e) => handleMouseDown(e, index, 'resize', 'nw')}
                            sx={{
                              position: 'absolute', top: -6, left: -6,
                              width: 12, height: 12, bgcolor: 'primary.main',
                              borderRadius: '50%', cursor: 'nwse-resize', border: '2px solid white'
                            }}
                          />
                          <Box
                            onMouseDown={(e) => handleMouseDown(e, index, 'resize', 'ne')}
                            sx={{
                              position: 'absolute', top: -6, right: -6,
                              width: 12, height: 12, bgcolor: 'primary.main',
                              borderRadius: '50%', cursor: 'nesw-resize', border: '2px solid white'
                            }}
                          />
                          <Box
                            onMouseDown={(e) => handleMouseDown(e, index, 'resize', 'sw')}
                            sx={{
                              position: 'absolute', bottom: -6, left: -6,
                              width: 12, height: 12, bgcolor: 'primary.main',
                              borderRadius: '50%', cursor: 'nesw-resize', border: '2px solid white'
                            }}
                          />
                          <Box
                            onMouseDown={(e) => handleMouseDown(e, index, 'resize', 'se')}
                            sx={{
                              position: 'absolute', bottom: -6, right: -6,
                              width: 12, height: 12, bgcolor: 'primary.main',
                              borderRadius: '50%', cursor: 'nwse-resize', border: '2px solid white'
                            }}
                          />
                          
                          {/* Edge handles */}
                          <Box
                            onMouseDown={(e) => handleMouseDown(e, index, 'resize', 'n')}
                            sx={{
                              position: 'absolute', top: -4, left: '50%', transform: 'translateX(-50%)',
                              width: 20, height: 8, bgcolor: 'primary.main',
                              borderRadius: 1, cursor: 'ns-resize', border: '2px solid white'
                            }}
                          />
                          <Box
                            onMouseDown={(e) => handleMouseDown(e, index, 'resize', 's')}
                            sx={{
                              position: 'absolute', bottom: -4, left: '50%', transform: 'translateX(-50%)',
                              width: 20, height: 8, bgcolor: 'primary.main',
                              borderRadius: 1, cursor: 'ns-resize', border: '2px solid white'
                            }}
                          />
                          <Box
                            onMouseDown={(e) => handleMouseDown(e, index, 'resize', 'w')}
                            sx={{
                              position: 'absolute', left: -4, top: '50%', transform: 'translateY(-50%)',
                              width: 8, height: 20, bgcolor: 'primary.main',
                              borderRadius: 1, cursor: 'ew-resize', border: '2px solid white'
                            }}
                          />
                          <Box
                            onMouseDown={(e) => handleMouseDown(e, index, 'resize', 'e')}
                            sx={{
                              position: 'absolute', right: -4, top: '50%', transform: 'translateY(-50%)',
                              width: 8, height: 20, bgcolor: 'primary.main',
                              borderRadius: 1, cursor: 'ew-resize', border: '2px solid white'
                            }}
                          />
                        </>
                      )}
                    </Box>
                  ))}
                </Box>
              ) : (
                <Paper sx={{ p: 8, textAlign: 'center', maxWidth: 400 }}>
                  <CloudUpload sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Upload Background Image
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Start by uploading a background image for your rich menu. Use PNG or JPEG format, max 1MB.
                  </Typography>
                  <Button
                    variant="contained"
                    component="label"
                    startIcon={<CloudUpload />}
                  >
                    Upload Image
                    <input
                      type="file"
                      hidden
                      accept="image/png,image/jpeg"
                      onChange={handleImageUpload}
                    />
                  </Button>
                </Paper>
              )}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button 
          variant="contained" 
          onClick={handleSave}
          disabled={!form.name || !form.chat_bar_text || saving}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          {saving ? 'Saving...' : mode === 'create' ? 'Create Rich Menu' : 'Update Rich Menu'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
